import React, { useMemo, useState, useRef, useCallback } from "react";
import { useUnifiedService } from "../../../utilities/contexts/UnifiedServiceContext";
import { GarmentMeasurementDetail } from "../../../utilities/Api/UnifiedApiClient";
import * as XLSX from "xlsx";

const UnitToggle: React.FC<{
  selectedUnit: "cm" | "inch";
  onUnitChange: (unit: "cm" | "inch") => void;
}> = ({ selectedUnit, onUnitChange }) => {
  return (
    <div className="flex items-center p-1 border border-gray-200 rounded-full bg-gray-50">
      {(["cm", "inch"] as const).map((unit) => (
        <button
          key={unit}
          onClick={() => onUnitChange(unit)}
          className={`px-3 py-2 rounded-full text-xs font-medium transition-all duration-300
            ${selectedUnit === unit ? "bg-white text-primary shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
        >
          {unit.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

const DisableableWrapper: React.FC<{ children: React.ReactNode; disabled: boolean }> = ({ children, disabled }) => {
  if (!disabled) return <>{children}</>;

  return (
    <div className="relative">
      {children}
      <div className="absolute inset-0 z-10 cursor-not-allowed bg-white/50 bg-opacity-60"></div>
    </div>
  );
};

const ImageStatusIcon: React.FC<{ status: "pending" | "processing" | "success" | "error" }> = ({ status }) => {
  const getIcon = () => {
    switch (status) {
      case "processing":
        return <div className="w-4 h-4 border-2 border-blue-500 rounded-full border-t-transparent animate-spin" />;
      case "success":
        return <div className="flex items-center justify-center w-4 h-4 text-xs text-white bg-green-500 rounded-full">✓</div>;
      case "error":
        return <div className="flex items-center justify-center w-4 h-4 text-xs text-white bg-red-500 rounded-full">✕</div>;
      case "pending":
        return <div className="w-4 h-4 bg-gray-300 rounded-full" />;
      default:
        return null;
    }
  };

  return <div className="absolute p-1 bg-white rounded-full shadow-sm top-2 right-2">{getIcon()}</div>;
};

const formatSizeValue = (value: number): string => {
  if (typeof value !== "number" || isNaN(value)) return "0.00";
  return Number(value).toFixed(2);
};

const formatLabel = (key: string): string => {
  if (!key) return "";
  const formatted = key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .trim();
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const isHorizontalMeasurement = (detail: GarmentMeasurementDetail): boolean => {
  if (!detail?.Points) return true;
  const xDiff = Math.abs(detail.Points.Point2.X - detail.Points.Point1.X);
  const yDiff = Math.abs(detail.Points.Point2.Y - detail.Points.Point1.Y);
  return xDiff > yDiff;
};

const hasValidCoordinates = (detail: GarmentMeasurementDetail): boolean => {
  if (!detail?.Points) {
    return false;
  }
  const { Point1, Point2 } = detail.Points;

  const isValid = !(
    (Point1.X === 0 && Point2.X === 0 && Point1.Y === 0 && Point2.Y === 0) ||
    Point1.X === undefined ||
    Point2.X === undefined ||
    Point1.Y === undefined ||
    Point2.Y === undefined ||
    typeof Point1.X !== "number" ||
    typeof Point1.Y !== "number" ||
    typeof Point2.X !== "number" ||
    typeof Point2.Y !== "number" ||
    isNaN(Point1.X) ||
    isNaN(Point1.Y) ||
    isNaN(Point2.X) ||
    isNaN(Point2.Y)
  );

  return isValid;
};

const InteractiveGarmentMeasurementResult: React.FC<{
  garmentResult: any;
  currentImage: string;
  currentImageIndex: number;
}> = ({ garmentResult, currentImage, currentImageIndex }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageDimensions({
      width: img.naturalWidth || 1200,
      height: img.naturalHeight || 1200,
    });
  };

  const getMeasurementLines = useCallback(() => {
    if (!garmentResult?.Measurements) {
      return null;
    }

    const { Measurements } = garmentResult;
    const lines = [];

    const containerWidth = containerRef.current?.offsetWidth || 240;
    const containerHeight = containerRef.current?.offsetHeight || 400;

    const imageAspectRatio = imageDimensions.width / imageDimensions.height;
    let displayedImageWidth, displayedImageHeight;

    if (containerWidth / containerHeight > imageAspectRatio) {
      displayedImageHeight = containerHeight;
      displayedImageWidth = containerHeight * imageAspectRatio;
    } else {
      displayedImageWidth = containerWidth;
      displayedImageHeight = containerWidth / imageAspectRatio;
    }

    const offsetX = (containerWidth - displayedImageWidth) / 2;
    const offsetY = (containerHeight - displayedImageHeight) / 2;

    for (const [key, measurement] of Object.entries(Measurements)) {
      const measurementDetail = measurement as GarmentMeasurementDetail;
      if (!measurementDetail.Points || !hasValidCoordinates(measurementDetail)) continue;

      const isHorizontal = isHorizontalMeasurement(measurementDetail);

      const x1Norm = measurementDetail.Points.Point1.X / imageDimensions.width;
      const x2Norm = measurementDetail.Points.Point2.X / imageDimensions.width;
      const y1Norm = measurementDetail.Points.Point1.Y / imageDimensions.height;
      const y2Norm = measurementDetail.Points.Point2.Y / imageDimensions.height;

      const x1Container = offsetX + x1Norm * displayedImageWidth;
      const x2Container = offsetX + x2Norm * displayedImageWidth;
      const y1Container = offsetY + y1Norm * displayedImageHeight;
      const y2Container = offsetY + y2Norm * displayedImageHeight;

      const x1Percent = (x1Container / containerWidth) * 100;
      const x2Percent = (x2Container / containerWidth) * 100;
      const y1Percent = (y1Container / containerHeight) * 100;
      const y2Percent = (y2Container / containerHeight) * 100;

      if (isHorizontal) {
        lines.push(
          <div
            key={key}
            className="absolute z-10 border-t-2 border-white border-dotted"
            style={{
              top: `${y1Percent}%`,
              left: `${x1Percent}%`,
              width: `${Math.abs(x2Percent - x1Percent)}%`,
              height: "0px",
            }}
          >
            <div className="absolute w-1.5 h-1.5 bg-white rounded-full -left-0.5 -top-1"></div>
            <div className="absolute w-1.5 h-1.5 bg-white rounded-full -right-0.5 -top-1"></div>
          </div>
        );
      } else {
        lines.push(
          <div
            key={key}
            className="absolute z-10 border-l-2 border-white border-dotted"
            style={{
              left: `${x1Percent}%`,
              top: `${y1Percent}%`,
              height: `${Math.abs(y2Percent - y1Percent)}%`,
              width: "0px",
            }}
          >
            <div className="absolute w-1.5 h-1.5 bg-white rounded-full -left-1 -top-0.5"></div>
            <div className="absolute w-1.5 h-1.5 bg-white rounded-full -left-1 -bottom-0.5"></div>
          </div>
        );
      }
    }

    return <div className="absolute inset-0 pointer-events-none">{lines}</div>;
  }, [garmentResult, imageDimensions]);

  if (!currentImage) return null;

  return (
    <div className="flex items-center justify-center w-fit" ref={containerRef}>
      <div className="relative grid place-content-center w-[15rem] h-[25rem] bg-white border border-gray-200 rounded-md overflow-hidden">
        <img src={currentImage} alt={`Uploaded garment ${currentImageIndex + 1}`} className="object-contain w-[15rem] h-[25rem]" onLoad={handleImageLoad} loading="eager" decoding="async" />
        {getMeasurementLines()}
      </div>
    </div>
  );
};

const ExportResults: React.FC<{
  measurements: any;
  measurementUnit: "cm" | "inch";
  allMeasurements?: any[];
  fileName?: string;
}> = ({ measurements, measurementUnit, allMeasurements = [], fileName = "garment-measurements" }) => {
  const [selectedFormat, setSelectedFormat] = useState<"json" | "text" | "csv" | "xlsx">("json");

  const formatMeasurementName = (key: string): string => {
    if (!key) return "";
    const formatted = key
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .trim();
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const convertMeasurements = (measurement: any) => {
    if (!measurement) return null;

    if (measurementUnit === "cm") {
      return measurement;
    }

    const CM_TO_INCH = 0.393701;
    const converted: any = {
      Measurements: {},
      MetaData: measurement.MetaData,
    };

    for (const key in measurement.Measurements) {
      const measurementItem = measurement.Measurements[key];
      converted.Measurements[key] = {
        ...measurementItem,
        Size: +(measurementItem.Size * CM_TO_INCH).toFixed(2),
      };
    }

    return converted;
  };

  const prepareMeasurementsForExport = (measurement: any) => {
    const data: Record<string, number> = {};
    if (measurement.Measurements) {
      for (const key in measurement.Measurements) {
        const measurementItem = measurement.Measurements[key];
        if (typeof measurementItem.Size === "number" && measurementItem.Size !== 0) {
          data[key] = measurementItem.Size;
        }
      }
    }
    return data;
  };

  const generateContent = (format: "json" | "text" | "csv" | "xlsx"): string => {
    const dataToExport = convertMeasurements(measurements);
    const measurementData = prepareMeasurementsForExport(dataToExport);
    const hasMeasurements = Object.keys(measurementData).length > 0;

    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();

    switch (format) {
      case "json":
        return hasMeasurements ? JSON.stringify(dataToExport, null, 2) : JSON.stringify({ note: "No measurement data available for this item" }, null, 2);
      case "text":
        return [
          "===== GARMENT MEASUREMENT RESULTS =====",
          `Date: ${currentDate}`,
          `Time: ${currentTime}`,
          "---------------------------------------",
          hasMeasurements
            ? Object.entries(measurementData)
                .map(([key, value]) => `${formatMeasurementName(key).padEnd(18)}: ${value} ${measurementUnit}`)
                .join("\n")
            : "No measurement data available for this item",
          "---------------------------------------",
          `Measurement Unit: ${measurementUnit.toUpperCase()}`,
          "=======================================",
        ].join("\n");
      case "csv":
        const headers = ["Measurement", "Value", "Unit"];
        let rows: string[] = [];

        if (hasMeasurements) {
          rows = Object.entries(measurementData).map(([key, value]) => `"${formatMeasurementName(key)}","${value}","${measurementUnit}"`);
        } else {
          rows = [`"Note","No measurement data available for this item",""`];
        }

        return [`# Garment Measurement Results`, `# Exported Date: ${currentDate} ${currentTime}`, `# Measurement Unit: ${measurementUnit}`, "", headers.join(","), ...rows].join("\n");
      case "xlsx":
        return JSON.stringify(hasMeasurements ? measurementData : { note: "No measurement data available for this item" });
      default:
        return JSON.stringify(dataToExport);
    }
  };

  const getMimeType = (format: "json" | "text" | "csv" | "xlsx"): string => {
    switch (format) {
      case "json":
        return "application/json";
      case "text":
        return "text/plain";
      case "csv":
        return "text/csv";
      case "xlsx":
        return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      default:
        return "application/json";
    }
  };

  const getFileExtension = (format: "json" | "text" | "csv" | "xlsx"): string => {
    switch (format) {
      case "json":
        return "json";
      case "text":
        return "txt";
      case "csv":
        return "csv";
      case "xlsx":
        return "xlsx";
      default:
        return "json";
    }
  };

  const handleExport = () => {
    try {
      if (selectedFormat === "xlsx") {
        const dataToExport = convertMeasurements(measurements);
        const measurementData = prepareMeasurementsForExport(dataToExport);
        const hasMeasurements = Object.keys(measurementData).length > 0;

        let tableData;
        if (hasMeasurements) {
          tableData = Object.entries(measurementData).map(([key, value]) => ({
            Measurement: formatMeasurementName(key),
            Value: value,
            Unit: measurementUnit,
          }));
        } else {
          tableData = [
            {
              Measurement: "Note",
              Value: "No measurement data available for this item",
              Unit: "",
            },
          ];
        }

        const ws = XLSX.utils.json_to_sheet(tableData);

        ws["!cols"] = [{ wch: 20 }, { wch: hasMeasurements ? 10 : 35 }, { wch: 8 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Measurements");

        const measurementInfoSheet = XLSX.utils.aoa_to_sheet([
          ["Garment Measurement Results"],
          [""],
          ["Exported Date", new Date().toLocaleString()],
          ["Measurement Unit", measurementUnit],
          ["Item", fileName.replace("garment-measurements-", "")],
          [""],
          ["Status", hasMeasurements ? "Data available" : "No measurement data available"],
        ]);

        XLSX.utils.book_append_sheet(wb, measurementInfoSheet, "Info");

        XLSX.writeFile(wb, `${fileName}.xlsx`);

        return;
      }

      const content = generateContent(selectedFormat);
      const mimeType = getMimeType(selectedFormat);
      const fileExtension = getFileExtension(selectedFormat);

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `${fileName}.${fileExtension}`;
      link.click();

      URL.revokeObjectURL(url);
    } catch (error: any) {
      alert("Failed to export file. Please try again.");
    }
  };

  const handleExportAll = () => {
    try {
      const validMeasurements = allMeasurements && allMeasurements.length > 0 ? allMeasurements.filter((m) => m !== null) : [];

      if (validMeasurements.length === 0) {
        alert("No valid measurements to export");
        return;
      }

      if (selectedFormat === "xlsx") {
        const wb = XLSX.utils.book_new();

        const allMeasurementsData: any[] = [];
        const allDetailData: any[] = [];

        if (validMeasurements.length > 0) {
          validMeasurements.forEach((measurement, index) => {
            if (!measurement) return;

            const convertedMeasurement = convertMeasurements(measurement);
            if (!convertedMeasurement) return;

            const measurementData = prepareMeasurementsForExport(convertedMeasurement);
            const hasMeasurements = Object.keys(measurementData).length > 0;

            const measurementRow: Record<string, any> = {
              Item: `Item ${index + 1}`,
              Unit: measurementUnit,
            };

            if (hasMeasurements) {
              Object.entries(measurementData).forEach(([key, value]) => {
                measurementRow[formatMeasurementName(key)] = value;
              });
            } else {
              measurementRow["Note"] = "No measurement data available for this item";
            }

            allMeasurementsData.push(measurementRow);

            if (hasMeasurements) {
              Object.entries(measurementData).forEach(([key, value]) => {
                allDetailData.push({
                  ItemNumber: index + 1,
                  Measurement: formatMeasurementName(key),
                  Value: value,
                  Unit: measurementUnit,
                });
              });
            } else {
              allDetailData.push({
                ItemNumber: index + 1,
                Measurement: "Note",
                Value: "No measurement data available for this item",
                Unit: "",
              });
            }

            if (hasMeasurements) {
              const itemTableData = Object.entries(measurementData).map(([key, value]) => ({
                Measurement: formatMeasurementName(key),
                Value: value,
                Unit: measurementUnit,
              }));

              if (itemTableData.length > 0) {
                const itemWs = XLSX.utils.json_to_sheet(itemTableData);
                itemWs["!cols"] = [{ wch: 20 }, { wch: 10 }, { wch: 8 }];
                XLSX.utils.book_append_sheet(wb, itemWs, `Item ${index + 1}`);
              }
            } else {
              const emptyItemData = [
                {
                  Measurement: "Note",
                  Value: "No measurement data available for this item",
                  Unit: "",
                },
              ];
              const itemWs = XLSX.utils.json_to_sheet(emptyItemData);
              itemWs["!cols"] = [{ wch: 20 }, { wch: 35 }, { wch: 8 }];
              XLSX.utils.book_append_sheet(wb, itemWs, `Item ${index + 1}`);
            }
          });
        }

        if (allMeasurementsData.length > 0) {
          const allMeasurementsWs = XLSX.utils.json_to_sheet(allMeasurementsData);

          const headers = Object.keys(allMeasurementsData[0]);
          const colWidths = headers.map((header) => ({ wch: Math.max(10, header.length * 1.2) }));
          allMeasurementsWs["!cols"] = colWidths;
          XLSX.utils.book_append_sheet(wb, allMeasurementsWs, "All Measurements");
        }

        if (allDetailData.length > 0) {
          const detailsWs = XLSX.utils.json_to_sheet(allDetailData);
          detailsWs["!cols"] = [{ wch: 10 }, { wch: 20 }, { wch: 10 }, { wch: 8 }];
          XLSX.utils.book_append_sheet(wb, detailsWs, "Details");
        }

        const allItemsInfoSheet = XLSX.utils.aoa_to_sheet([
          ["Garment Measurement Results - All Items"],
          [""],
          ["Exported Date", new Date().toLocaleString()],
          ["Measurement Unit", measurementUnit],
          ["Number of Items", validMeasurements.length.toString()],
          [""],
          ["Note", "Please check 'All Measurements' and 'Details' sheets for complete data"],
        ]);
        XLSX.utils.book_append_sheet(wb, allItemsInfoSheet, "Info");

        XLSX.writeFile(wb, `garment-measurements-all.xlsx`);

        return;
      }

      const currentDate = new Date().toLocaleDateString();
      const currentTime = new Date().toLocaleTimeString();

      let allContent = "";

      if (validMeasurements.length > 0) {
        validMeasurements.forEach((measurement, index) => {
          if (!measurement) return;

          const convertedMeasurement = convertMeasurements(measurement);
          if (!convertedMeasurement) return;

          const measurementData = prepareMeasurementsForExport(convertedMeasurement);

          switch (selectedFormat) {
            case "json":
              allContent = allContent || "[\n";
              const jsonData = Object.keys(measurementData).length > 0 ? { ...measurementData, itemIndex: index + 1 } : { itemIndex: index + 1, note: "No measurement data available for this item" };
              allContent += (index > 0 ? ",\n" : "") + "  " + JSON.stringify(jsonData, null, 2).replace(/\n/g, "\n  ");
              if (index === validMeasurements.length - 1) {
                allContent += "\n]";
              }
              break;

            case "text":
              if (index > 0) {
                allContent += "\n\n";
              }

              const hasMeasurements = Object.keys(measurementData).length > 0;

              allContent += [
                `===== GARMENT MEASUREMENT ITEM ${index + 1} =====`,
                "---------------------------------------",
                hasMeasurements
                  ? Object.entries(measurementData)
                      .map(([key, value]) => `${formatMeasurementName(key).padEnd(20)}: ${value} ${measurementUnit}`)
                      .join("\n")
                  : "No measurement data available for this item",
                "---------------------------------------",
              ].join("\n");
              break;

            case "csv":
              if (index === 0) {
                allContent =
                  [`# Garment Measurement Results - All Items`, `# Exported Date: ${currentDate} ${currentTime}`, `# Measurement Unit: ${measurementUnit}`, "", "Item,Measurement,Value,Unit"].join(
                    "\n"
                  ) + "\n";
              }

              if (Object.keys(measurementData).length > 0) {
                Object.entries(measurementData).forEach(([key, value]) => {
                  allContent += `${index + 1},"${formatMeasurementName(key)}","${value}","${measurementUnit}"\n`;
                });
              } else {
                allContent += `${index + 1},"NOTE","No measurement data available for this item",""\n`;
              }
              break;
          }
        });
      }

      if (selectedFormat === "text" && allContent) {
        allContent = [
          "====== ALL GARMENT MEASUREMENTS ======",
          `Date: ${currentDate}`,
          `Time: ${currentTime}`,
          `Items Count: ${validMeasurements.length}`,
          `Measurement Unit: ${measurementUnit.toUpperCase()}`,
          "=======================================",
          "",
          allContent,
          "",
          "=======================================",
        ].join("\n");
      }

      const mimeType = getMimeType(selectedFormat);
      const fileExtension = getFileExtension(selectedFormat);

      const blob = new Blob([allContent], { type: mimeType });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `garment-measurements-all.${fileExtension}`;
      link.click();

      URL.revokeObjectURL(url);
    } catch (error: any) {
      alert("Failed to export all items. Please try again.");
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4 bg-white border border-gray-200 rounded-lg">
      <h3 className="text-lg font-medium text-secondary">Export Results</h3>

      <div className="flex flex-col gap-4">
        <select
          value={selectedFormat}
          onChange={(e) => setSelectedFormat(e.target.value as any)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        >
          <option value="json">JSON</option>
          <option value="text">TEXT</option>
          <option value="csv">CSV</option>
          <option value="xlsx">XLSX</option>
        </select>

        <div className="flex flex-col gap-2 md:flex-row">
          <button onClick={handleExport} className="flex items-center justify-center w-full gap-2 px-4 py-2 text-white transition-colors rounded-lg bg-primary hover:bg-primary/90">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Export as {selectedFormat.toUpperCase()} ({measurementUnit})
          </button>

          <button
            onClick={handleExportAll}
            className="flex items-center justify-center w-full gap-2 px-4 py-2 text-gray-700 transition-colors bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!allMeasurements || allMeasurements.length <= 1}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Export All Items
          </button>
        </div>
      </div>
    </div>
  );
};

const GarmentMeasurementResults: React.FC = () => {
  const { garmentMeasurement, serviceInterface } = useUnifiedService();
  const [measurementUnit, setMeasurementUnit] = useState<"cm" | "inch">("cm");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const getCurrentImageResult = useCallback(() => {
    if (garmentMeasurement.allMeasurements && garmentMeasurement.allMeasurements.length > currentImageIndex) {
      const currentResult = garmentMeasurement.allMeasurements[currentImageIndex];
      return currentResult;
    }

    return garmentMeasurement.result;
  }, [garmentMeasurement.allMeasurements, garmentMeasurement.result, currentImageIndex]);

  const convertedMeasurements = useMemo(() => {
    const currentResult = getCurrentImageResult();
    if (!currentResult) return null;

    if (measurementUnit === "cm") {
      return currentResult;
    }

    const CM_TO_INCH = 0.393701;
    const converted: any = {
      Measurements: {},
      MetaData: currentResult.MetaData,
    };

    for (const key in currentResult.Measurements) {
      const measurement = currentResult.Measurements[key];
      converted.Measurements[key] = {
        ...measurement,
        Size: +(measurement.Size * CM_TO_INCH).toFixed(2),
      };
    }

    return converted;
  }, [getCurrentImageResult, measurementUnit]);

  const measurementItems = useMemo(() => {
    if (!convertedMeasurements?.Measurements) {
      return [];
    }

    const items = [];
    for (const key in convertedMeasurements.Measurements) {
      const measurement = convertedMeasurements.Measurements[key];

      if (!measurement || measurement.Size === undefined || measurement.Size === 0) {
        continue;
      }

      const label = formatLabel(key);
      const formattedValue = formatSizeValue(measurement.Size);

      items.push({ key, label, value: formattedValue, points: measurement.Points });
    }

    return items.sort((a, b) => {
      if (a.key.includes("Top") && !b.key.includes("Top")) return -1;
      if (!a.key.includes("Top") && b.key.includes("Top")) return 1;
      if (a.key.includes("Bottom") && !b.key.includes("Bottom")) return 1;
      if (!a.key.includes("Bottom") && b.key.includes("Bottom")) return -1;
      return 0;
    });
  }, [convertedMeasurements]);

  const hasMeasurements = measurementItems.length > 0;

  const validImages = useMemo(() => {
    const images = [];

    if (garmentMeasurement.images && garmentMeasurement.images.length > 0) {
      garmentMeasurement.images.forEach((img) => {
        if (img.base64) {
          // Use JPEG format for better compression
          images.push(`data:image/jpeg;base64,${img.base64}`);
        } else if (img.preview) {
          images.push(img.preview);
        }
      });
    }

    if (images.length === 0) {
      if (garmentMeasurement.image.base64) {
        // Use JPEG format for better compression
        images.push(`data:image/jpeg;base64,${garmentMeasurement.image.base64}`);
      } else if (garmentMeasurement.image.preview) {
        images.push(garmentMeasurement.image.preview);
      }
    }

    return images;
  }, [garmentMeasurement.images, garmentMeasurement.image]);

  const currentImage = validImages[currentImageIndex] || "";

  const handleImageClick = (index: number) => {
    setCurrentImageIndex(index);
  };

  const getProcessingStatus = (index: number) => {
    if (garmentMeasurement.images && garmentMeasurement.images[index]) {
      const imageState = garmentMeasurement.images[index];
      if (imageState.error) return "error";
      if (garmentMeasurement.result) return "success";
      if (garmentMeasurement.loadingResult) return "processing";
      return "pending";
    }

    if (garmentMeasurement.image.error) return "error";
    if (garmentMeasurement.result) return "success";
    if (garmentMeasurement.loadingResult) return "processing";
    return "pending";
  };

  const getMeasurementError = () => {
    if (garmentMeasurement.images && garmentMeasurement.images[currentImageIndex]) {
      return garmentMeasurement.images[currentImageIndex].error || null;
    }

    return garmentMeasurement.image.error || null;
  };

  if (garmentMeasurement.loadingResult) {
    return (
      <div className="flex flex-col items-center justify-center w-full gap-4 p-8">
        <div className="w-8 h-8 border-4 rounded-full border-primary border-t-transparent animate-spin"></div>
        <p className="text-gray-600">Processing your garment measurements...</p>
      </div>
    );
  }

  const measurementError = getMeasurementError();

  const allImagesFailed =
    garmentMeasurement.processingStatus && Object.values(garmentMeasurement.processingStatus).length > 0 && Object.values(garmentMeasurement.processingStatus).every((status) => status === "error");

  const hasAnyResults = garmentMeasurement.result || (garmentMeasurement.allMeasurements && garmentMeasurement.allMeasurements.some((result) => result !== null));

  if (allImagesFailed && !hasAnyResults) {
    return (
      <div className="flex flex-col items-center w-full gap-4">
        <div className="max-w-2xl p-8 text-center border border-red-200 bg-red-50 rounded-xl">
          <h3 className="mb-2 text-lg font-medium text-red-800">Processing Failed</h3>
          <p className="mb-4 text-red-600">All images failed to process. This might be due to:</p>
          <ul className="mb-4 text-sm text-left text-red-600 list-disc list-inside">
            <li>Images not meeting quality requirements</li>
            <li>Missing A4 reference paper</li>
            <li>Network connectivity issues</li>
            <li>Server processing errors</li>
          </ul>
          <p className="text-sm text-gray-600">Please check your images and try again, or contact support if the problem persists.</p>
        </div>
      </div>
    );
  }

  if (measurementError) {
    return (
      <div className="flex flex-col items-center w-full gap-4">
        <div className="max-w-2xl p-8 text-center border border-red-200 bg-red-50 rounded-xl">
          <h3 className="mb-2 text-lg font-medium text-red-800">Measurement Failed</h3>
          <p className="text-red-600">{measurementError}</p>
        </div>
      </div>
    );
  }

  if (!garmentMeasurement.result) {
    return (
      <div className="flex flex-col items-center w-full gap-4">
        <div className="max-w-2xl p-8 text-center border border-gray-200 bg-gray-50 rounded-xl">
          <h3 className="mb-2 text-lg font-medium text-gray-800">No Results</h3>
          <p className="text-gray-600">No measurement results available</p>
        </div>
      </div>
    );
  }

  const safeGarmentResult = convertedMeasurements || {
    Measurements: {},
    MetaData: { Date: new Date().toISOString().split("T")[0], FileNames: [] },
  };

  const renderMeasurementsOrError = () => {
    if (measurementError) {
      return <div className="py-4 text-center text-red-600">Error: {measurementError}</div>;
    }

    if (!hasMeasurements) {
      return <div className="py-4 text-center text-gray-500">No measurements available for this image</div>;
    }

    return null;
  };

  return (
    <div className="flex flex-col items-center w-full gap-4">
      <div className="text-center">
        <h2 className="mb-1 text-2xl font-medium text-secondary">Garment Measurement Results</h2>
        <p className="text-gray-600">Your garment measurements are ready</p>
      </div>

      <div className="hidden max-w-5xl border border-gray-200 rounded-lg w-fit md:flex md:gap-4 md:flex-col md:items-center">
        <div className="flex justify-center gap-4">
          <div className="p-4 w-fit">
            {currentImage ? (
              <InteractiveGarmentMeasurementResult garmentResult={safeGarmentResult} currentImage={currentImage} currentImageIndex={currentImageIndex} />
            ) : (
              <div className="flex items-center justify-center w-[15rem] h-[25rem] bg-gray-100 border border-gray-200 rounded-lg">
                <div className="text-center text-gray-500">
                  <p>No image available</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex-1 h-full flex flex-col items-center p-4 max-w-[30rem] xl:min-w-[30rem]">
            <div className="flex justify-center w-full mb-3">
              <DisableableWrapper disabled={!hasMeasurements}>
                <UnitToggle selectedUnit={measurementUnit} onUnitChange={setMeasurementUnit} />
              </DisableableWrapper>
            </div>
            <div className="grid grid-cols-1 gap-4 overflow-y-auto max-h-[25rem] w-full">
              {renderMeasurementsOrError() ||
                measurementItems.map((item, index) => (
                  <div key={index} className="flex items-center justify-between gap-4 p-4 border border-gray-200 rounded-lg">
                    <span className="text-sm font-medium text-gray-800">{item.label}</span>
                    <div className="flex items-end min-w-24">
                      <span className="font-bold text-primary">{item.value}</span>
                      <span className="ml-1 text-sm text-gray-500">{measurementUnit}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
        <div className="w-full px-6 py-4">
          <DisableableWrapper disabled={!hasMeasurements}>
            <ExportResults
              measurements={safeGarmentResult}
              measurementUnit={measurementUnit}
              allMeasurements={garmentMeasurement.allMeasurements || []}
              fileName={`garment-measurements-${currentImageIndex + 1}`}
            />
          </DisableableWrapper>
        </div>
      </div>

      <div className="flex flex-col w-full gap-10 md:hidden">
        <div className="flex flex-col items-center gap-4">
          <div className="flex justify-center p-4 border border-gray-200 rounded-lg w-fit">
            {currentImage ? (
              <InteractiveGarmentMeasurementResult garmentResult={safeGarmentResult} currentImage={currentImage} currentImageIndex={currentImageIndex} />
            ) : (
              <div className="flex items-center justify-center w-[15rem] h-[25rem] bg-gray-100 border border-gray-200 rounded-lg">
                <div className="text-center text-gray-500">
                  <p>No image available</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col items-center w-full gap-4">
            <div className="flex justify-center w-full mb-3">
              <DisableableWrapper disabled={!hasMeasurements}>
                <UnitToggle selectedUnit={measurementUnit} onUnitChange={setMeasurementUnit} />
              </DisableableWrapper>
            </div>
            <div className="grid grid-cols-1 gap-3 overflow-y-auto max-h-[25rem] w-full">
              {renderMeasurementsOrError() ||
                measurementItems.map((item, index) => (
                  <div key={index} className="flex flex-col p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-800">{item.label}</span>
                      <div className="flex items-end min-w-24">
                        <span className="font-bold text-primary">{item.value}</span>
                        <span className="ml-1 text-sm text-gray-500">{measurementUnit}</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
        <DisableableWrapper disabled={!hasMeasurements}>
          <ExportResults
            measurements={safeGarmentResult}
            measurementUnit={measurementUnit}
            allMeasurements={garmentMeasurement.allMeasurements || []}
            fileName={`garment-measurements-${currentImageIndex + 1}`}
          />
        </DisableableWrapper>
      </div>

      {validImages.length > 1 && (
        <div className="flex flex-col w-full max-w-5xl border border-gray-200 divide-y rounded-lg">
          <div className="flex items-center justify-between p-4">
            <h3 className="text-lg font-medium text-secondary">All Uploaded Images</h3>
          </div>
          <div className="p-4">
            <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 place-items-center">
              {validImages.map((image, index) => {
                const isSelected = index === currentImageIndex;
                const status = getProcessingStatus(index);
                const error = garmentMeasurement.images?.[index]?.error || (index === 0 ? garmentMeasurement.image.error : null);

                return (
                  <div
                    key={index}
                    className={`relative cursor-pointer transition-all border rounded-lg w-full max-w-[15rem] overflow-hidden ${isSelected ? "ring-2 ring-primary scale-[1.02]" : "border-gray-200"}`}
                    onClick={() => handleImageClick(index)}
                  >
                    <div className="relative grid place-content-center w-full h-[20rem]">
                      <img src={image} alt={`Garment ${index + 1}`} className="object-contain w-full h-full" loading="lazy" decoding="async" />
                    </div>
                    <div className="absolute grid w-6 h-6 text-sm text-gray-700 bg-white border border-gray-200 rounded-full place-content-center top-2 left-2">{index + 1}</div>
                    <ImageStatusIcon status={status} />

                    {error && <div className="absolute bottom-0 left-0 right-0 p-2 text-xs text-white bg-red-500">{error}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GarmentMeasurementResults;
